import React, { useState, useEffect } from 'react';
import { useDataProvider, useNotify, useRefresh, useRecordContext } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { CloudDownload as SyncIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { apiClient } from '../../framework/api/api.client';

/**
 * Tenant Config Sync Component
 * 
 * Allows syncing tenant, brands, locations, and devices from parent Neo instance
 */
export const TenantConfigSync: React.FC = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  
  const [connections, setConnections] = useState<any[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<string | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [discoveredConfig, setDiscoveredConfig] = useState<any>(null);
  const [syncOptions, setSyncOptions] = useState({
    syncTenant: true,
    syncBrands: true,
    syncLocations: true,
    syncDevices: true,
    syncApplications: false,
    syncDashboards: false,
    syncDataModels: false
  });
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [selectedDashboards, setSelectedDashboards] = useState<string[]>([]);
  const [selectedDataModels, setSelectedDataModels] = useState<string[]>([]);
  
  useEffect(() => {
    loadConnections();
  }, []);
  
  const loadConnections = async () => {
    try {
      const { data } = await dataProvider.getList('connection', {
        filter: {},
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' }
      });
      
      // Filter for Neo Platform connections using correct field name
      const neoConnections = data.filter((conn: any) => 
        conn.connectionProviderKey === 'neo-platform'
      );
      
      console.log('All connections:', data);
      console.log('Neo Platform connections:', neoConnections);
      
      setConnections(neoConnections);
      if (neoConnections.length > 0) {
        setSelectedConnection(neoConnections[0].id);
      }
    } catch (error) {
      console.error('Failed to load connections:', error);
    }
  };
  
  const handleDiscover = async () => {
    if (!selectedConnection) {
      notify('Please select a parent connection', { type: 'warning' });
      return;
    }
    
    setDiscovering(true);
    try {
      // Backend now returns applications and dashboards in the discover response
      const config = await apiClient.call(`/sync/discover-tenant-config?connectionId=${selectedConnection}`);
      
      setDiscoveredConfig(config);
      
      const parts = [
        `${config.summary.brandsCount} brands`,
        `${config.summary.locationsCount} locations`,
        `${config.summary.devicesCount} devices`,
        `${config.summary.applicationsCount || 0} applications`,
        `${config.summary.dashboardsCount || 0} dashboards`,
        `${config.summary.dataModelsCount || 0} data models`
      ];
      
      notify(`Discovered: ${parts.join(', ')}`, { type: 'success' });
    } catch (error: any) {
      console.error('Failed to discover config:', error);
      notify(`Discovery failed: ${error.message}`, { type: 'error' });
    } finally {
      setDiscovering(false);
    }
  };
  
  const handleSync = async () => {
    if (!selectedConnection) {
      notify('Please select a parent connection', { type: 'warning' });
      return;
    }
    
    setSyncing(true);
    try {
      const results = await apiClient.call('/sync/sync-tenant-config', {
        method: 'POST',
        body: {
          connectionId: selectedConnection,
          ...syncOptions,
          applicationIds: selectedApps,
          dashboardIds: selectedDashboards,
          dataModelIds: selectedDataModels
        }
      });
      
      const summary = [
        syncOptions.syncTenant && results.tenant ? `Tenant: ${results.tenant.action}` : null,
        syncOptions.syncBrands ? `Brands: ${results.brands.length}` : null,
        syncOptions.syncLocations ? `Locations: ${results.locations.length}` : null,
        syncOptions.syncDevices ? `Devices: ${results.devices.length}` : null,
        syncOptions.syncApplications && results.applications ? `Applications: ${results.applications.length}` : null,
        syncOptions.syncDashboards && results.dashboards ? `Dashboards: ${results.dashboards.length}` : null,
        syncOptions.syncDataModels && results.dataModels ? `Data Models: ${results.dataModels.length}` : null,
      ].filter(Boolean).join(', ');
      
      notify(`✅ Sync complete! ${summary}`, { type: 'success' });
      
      if (results.errors && results.errors.length > 0) {
        notify(`⚠️ ${results.errors.length} errors occurred during sync`, { type: 'warning' });
      }
      
      refresh();
    } catch (error: any) {
      console.error('Failed to sync config:', error);
      notify(`Sync failed: ${error.message}`, { type: 'error' });
    } finally {
      setSyncing(false);
    }
  };
  
  if (!record?.id) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">
          Save the tenant first to sync configuration
        </Alert>
      </Box>
    );
  }
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Sync from Parent Instance
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
        Pull tenant configuration (brands, locations, devices) from a parent Neo platform instance
      </Typography>
      
      {connections.length === 0 ? (
        <Alert severity="warning">
          No parent Neo Platform connections found. Create a connection to the parent instance first.
        </Alert>
      ) : (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Parent Connection
              </Typography>
              <select
                value={selectedConnection || ''}
                onChange={(e) => setSelectedConnection(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  marginBottom: '16px'
                }}
              >
                {connections.map((conn) => (
                  <option key={conn.id} value={conn.id}>
                    {conn.name} ({conn.config?.instanceUrl})
                  </option>
                ))}
              </select>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleDiscover}
                  disabled={discovering || !selectedConnection}
                >
                  {discovering ? 'Discovering...' : 'Discover Configuration'}
                </Button>
              </Box>
              
              {discoveredConfig && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Available to Sync:</strong>
                  </Typography>
                  <Typography variant="body2">
                    • Tenant: {discoveredConfig.tenant?.name}
                  </Typography>
                  <Typography variant="body2">
                    • {discoveredConfig.summary.brandsCount} Brand(s)
                  </Typography>
                  <Typography variant="body2">
                    • {discoveredConfig.summary.locationsCount} Location(s)
                  </Typography>
                  <Typography variant="body2">
                    • {discoveredConfig.summary.devicesCount} Device(s)
                  </Typography>
                  {discoveredConfig.applications && (
                    <Typography variant="body2">
                      • {discoveredConfig.applications.length} Published Application(s)
                    </Typography>
                  )}
                  {discoveredConfig.dashboards && (
                    <Typography variant="body2">
                      • {discoveredConfig.dashboards.length} Published Dashboard(s)
                    </Typography>
                  )}
                  {discoveredConfig.dataModels && (
                    <Typography variant="body2">
                      • {discoveredConfig.dataModels.length} Published Data Model(s)
                    </Typography>
                  )}
                </Alert>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Sync Options
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={syncOptions.syncTenant}
                      onChange={(e) => setSyncOptions({ ...syncOptions, syncTenant: e.target.checked })}
                    />
                  }
                  label="Sync Tenant Information"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={syncOptions.syncBrands}
                      onChange={(e) => setSyncOptions({ ...syncOptions, syncBrands: e.target.checked })}
                    />
                  }
                  label="Sync Brands"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={syncOptions.syncLocations}
                      onChange={(e) => setSyncOptions({ ...syncOptions, syncLocations: e.target.checked })}
                    />
                  }
                  label="Sync Locations"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={syncOptions.syncDevices}
                      onChange={(e) => setSyncOptions({ ...syncOptions, syncDevices: e.target.checked })}
                    />
                  }
                  label="Sync Devices"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={syncOptions.syncApplications}
                      onChange={(e) => setSyncOptions({ ...syncOptions, syncApplications: e.target.checked })}
                    />
                  }
                  label="Sync Applications"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={syncOptions.syncDashboards}
                      onChange={(e) => setSyncOptions({ ...syncOptions, syncDashboards: e.target.checked })}
                    />
                  }
                  label="Sync Dashboards"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={syncOptions.syncDataModels}
                      onChange={(e) => setSyncOptions({ ...syncOptions, syncDataModels: e.target.checked })}
                    />
                  }
                  label="Sync Data Models"
                />
              </FormGroup>
              
              {/* Application Selection */}
              {syncOptions.syncApplications && discoveredConfig?.applications && discoveredConfig.applications.length > 0 && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Select Applications to Sync
                  </Typography>
                  <FormGroup>
                    {discoveredConfig.applications.map((app: any) => (
                      <FormControlLabel
                        key={app.id}
                        control={
                          <Checkbox
                            checked={selectedApps.includes(app.id)}
                            onChange={() => {
                              setSelectedApps(prev =>
                                prev.includes(app.id)
                                  ? prev.filter(id => id !== app.id)
                                  : [...prev, app.id]
                              );
                            }}
                          />
                        }
                        label={`${app.name} (v${app.currentVersion || 1})`}
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}
              
              {/* Dashboard Selection */}
              {syncOptions.syncDashboards && discoveredConfig?.dashboards && discoveredConfig.dashboards.length > 0 && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Select Dashboards to Sync
                  </Typography>
                  <FormGroup>
                    {discoveredConfig.dashboards.map((dashboard: any) => (
                      <FormControlLabel
                        key={dashboard.id}
                        control={
                          <Checkbox
                            checked={selectedDashboards.includes(dashboard.id)}
                            onChange={() => {
                              setSelectedDashboards(prev =>
                                prev.includes(dashboard.id)
                                  ? prev.filter(id => id !== dashboard.id)
                                  : [...prev, dashboard.id]
                              );
                            }}
                          />
                        }
                        label={`${dashboard.name} (v${dashboard.currentVersion || 1})`}
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}
              
              {/* Data Model Selection */}
              {syncOptions.syncDataModels && discoveredConfig?.dataModels && discoveredConfig.dataModels.length > 0 && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Select Data Models to Sync
                  </Typography>
                  <FormGroup>
                    {discoveredConfig.dataModels.map((dataModel: any) => (
                      <FormControlLabel
                        key={dataModel.id}
                        control={
                          <Checkbox
                            checked={selectedDataModels.includes(dataModel.id)}
                            onChange={() => {
                              setSelectedDataModels(prev =>
                                prev.includes(dataModel.id)
                                  ? prev.filter(id => id !== dataModel.id)
                                  : [...prev, dataModel.id]
                              );
                            }}
                          />
                        }
                        label={`${dataModel.name} (${dataModel.scope}, v${dataModel.currentVersion || 1})`}
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}
              
              <Button
                variant="contained"
                color="primary"
                startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                onClick={handleSync}
                disabled={syncing || !selectedConnection}
                sx={{ mt: 2 }}
                fullWidth
              >
                {syncing ? 'Syncing...' : 'Sync from Parent'}
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

